<script setup>
import dayjs from 'dayjs'
import { ref, computed } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { useAccountingStore } from '@/stores/accounting.store'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { infoBoxToneEnum } from '@/stores/portal.store'

import { useNotify } from '@/composables/notify'
import { useIsUpdated } from '@/composables/object'
import { useNavigator } from '@/composables/navigator'
import { useEscapeClose } from '@/composables/keyboard'
import { useDetectDevice } from '@/composables/device'
import { useClone } from '@/composables/object'
import { useTimeFields, isoToDate } from '@/composables/date-time'
import { formatCurrencyValue } from '@/composables/currency'
import { useExchangeRateEdit, isReferenceCurrency } from '@/composables/exchange-rate'
import { useLockedCostReportRow } from '@/composables/cost-report'
import { provideReadonlyForm } from '@/composables/readonly-form'

import FileUrlEdit from '@/components/shared/FileUrlEdit.vue'
import InputDateSelector from '@/components/shared/InputDateSelector.vue'
import NavigateBack from '@/components/shared/NavigateBack.vue'
import TimeSelector from '@/components/shared/TimeSelector.vue'
import CurrencyInput from '@/components/shared/CurrencyInput.vue'
import CurrencySelect from '@/components/shared/CurrencySelect.vue'
import ExchangeRatePanel from '@/components/shared/ExchangeRatePanel.vue'
import InfoBox from '@/components/shared/InfoBox.vue'
import FormSectionTitle from '@/components/shared/FormSectionTitle.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  project: {
    type: Object,
    default: null,
  },
})

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const { notifySuccess } = useNotify()

const accountingStore = useAccountingStore()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()

const form = ref(null)


const currentUserStaffName = computed(() =>
  planningStore.getServiceName(planningStore.getStaffById(webSiteStore.staffId))
)

const isNewRow = !props.modelValue.id
const newItemDefaults = () => ({
  project: props.project?.id ?? null,
  date: props.project ? isoToDate(props.project.start) : dayjs().format('YYYY-MM-DD'),
  ...(webSiteStore.hasStaff ? { staff: webSiteStore.staffId } : {}),
  ...(props.modelValue.type === accountingStore.costReportRowTypeEnum.EXPENSE ? { quantity: 1, unit_of_measure_ref: webSiteStore.getDefaultExtraUnitOfMeasure()?.id } : {}),
})

const editItem = ref(isNewRow ? { ...newItemDefaults(), ...useClone(props.modelValue) } : useClone(props.modelValue))

const { isUpdated, isUpdating } = useIsUpdated(editItem)

const { costReportRowTypeEnum } = accountingStore

const projectStaff = ref([])
const staffProjects = ref([])

let staffProjectsKey = null
const fetchStaffProjectsAsync = async() => {
  if (props.project || !editItem.value.staff) return
  const month = dayjs(editItem.value.date || undefined)
  const key = `${editItem.value.staff}|${month.format('YYYY-MM')}`
  if (key === staffProjectsKey) return
  staffProjectsKey = key
  staffProjects.value = await planningStore.fetchProjectsAsync({
    staff: editItem.value.staff,
    date_from: month.startOf('month').format('YYYY-MM-DD'),
    date_to: month.endOf('month').format('YYYY-MM-DD'),
  }) ?? []
}

planningStore.fetchStaffsAsync({}, true)
if (props.project) planningStore.fetchStaffsAsync({ all: true, project: props.project.id }).then((staff) => { projectStaff.value = staff })
accountingStore.fetchCostReportRowTagsAsync({}, true)
accountingStore.fetchCompanyDataAsync({}, true)
if (editItem.value.type === costReportRowTypeEnum.EXPENSE) fetchStaffProjectsAsync()

const onDateChange = (value) => {
  editItem.value.date = value
  fetchStaffProjectsAsync()
}
const onStaffChange = (value) => {
  editItem.value.staff = value
  fetchStaffProjectsAsync()
}

// ─── Time helpers ─────────────────────────────────────────────────────────────

const { timeToISO, onUpdateTime } = useTimeFields(editItem)

const defaultStartTime = computed(() =>
  (accountingStore.companyData?.default_start_time || '09:00:00').substring(0, 5)
)

const computeMinutesFromHHmm = (startStr, endStr) => {
  if (!startStr || !endStr) return null
  const [sh, sm] = startStr.split(':').map(Number)
  const [eh, em] = endStr.split(':').map(Number)
  let mins = (eh * 60 + em) - (sh * 60 + sm)
  if (mins < 0) mins += 24 * 60
  return mins
}

const minutesToHHmm = (startHHmm, totalMins) => {
  const [sh, sm] = startHHmm.split(':').map(Number)
  const total = sh * 60 + sm + totalMins
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

// For each time group: compute hours display from start+end, or fall back to stored minutes
const hoursDisplay = (startField, endField, minutesField) => computed(() => {
  const mins = computeMinutesFromHHmm(editItem.value[startField], editItem.value[endField]) ?? editItem.value[minutesField]
  return mins != null ? +(mins / 60).toFixed(2) : null
})

const workHours = hoursDisplay('work_start', 'work_end', 'work_minutes')
const reportedHours = hoursDisplay('reported_start', 'reported_end', 'reported_minutes')
const travelHours = hoursDisplay('travel_start', 'travel_end', 'travel_minutes')
const pauseHours = hoursDisplay('pause_start', 'pause_end', 'pause_minutes')

// Bumped on every start/end edit to remount the total-hours inputs:
// Quasar q-input type="number" keeps the last user-typed value in a non-reactive
// cache, so a programmatic update alone leaves the old hours displayed.
const hoursKey = ref(0)

// When start+end both set, sync minutes field. Called after onUpdateTime.
const syncMinutes = (startField, endField, minutesField) => {
  const mins = computeMinutesFromHHmm(editItem.value[startField], editItem.value[endField])
  if (mins !== null) editItem.value[minutesField] = mins
  hoursKey.value++
}

const onWorkStartUpdate = (val) => {
  onUpdateTime('work_start', val)
  if (val && !editItem.value.work_end) onUpdateTime('work_end', val)
  syncMinutes('work_start', 'work_end', 'work_minutes')
}
const onWorkEndUpdate = (val) => { onUpdateTime('work_end', val); syncMinutes('work_start', 'work_end', 'work_minutes') }

const onReportedStartUpdate = (val) => {
  onUpdateTime('reported_start', val)
  if (val && !editItem.value.reported_end) onUpdateTime('reported_end', val)
  syncMinutes('reported_start', 'reported_end', 'reported_minutes')
}
const onReportedEndUpdate = (val) => { onUpdateTime('reported_end', val); syncMinutes('reported_start', 'reported_end', 'reported_minutes') }

const onTravelStartUpdate = (val) => {
  onUpdateTime('travel_start', val)
  if (val && !editItem.value.travel_end) onUpdateTime('travel_end', val)
  syncMinutes('travel_start', 'travel_end', 'travel_minutes')
}
const onTravelEndUpdate = (val) => { onUpdateTime('travel_end', val); syncMinutes('travel_start', 'travel_end', 'travel_minutes') }

const onPauseStartUpdate = (val) => {
  onUpdateTime('pause_start', val)
  if (val && !editItem.value.pause_end) onUpdateTime('pause_end', val)
  syncMinutes('pause_start', 'pause_end', 'pause_minutes')
}
const onPauseEndUpdate = (val) => { onUpdateTime('pause_end', val); syncMinutes('pause_start', 'pause_end', 'pause_minutes') }

const MAX_HOURS = 24

// When user types total hours directly: always recalculate end from start (or default start)
const onHoursChange = (startField, endField, minutesField, val) => {
  if (val === null || val === '') { editItem.value[minutesField] = null; return }
  const mins = Math.min(Math.round(parseFloat(val) * 60), MAX_HOURS * 60)
  if (isNaN(mins) || mins <= 0) { editItem.value[minutesField] = null; return }
  editItem.value[minutesField] = mins
  if (!editItem.value[startField]) editItem.value[startField] = defaultStartTime.value
  editItem.value[endField] = minutesToHHmm(editItem.value[startField], mins)
}

// ─── Validation ────────────────────────────────────────────────────────────────

const requiredIfPaired = (counterpartField) => (val) => !editItem.value[counterpartField] || !!val || i18n.t('common.required')

const hasErrors = computed(() => {
  const { staff, type, work_start, work_end, reported_start, reported_end, travel_start, travel_end, pause_start, pause_end,
    work_minutes, reported_minutes, travel_minutes, pause_minutes,
    tag, quantity, notes } = editItem.value
  if (type === costReportRowTypeEnum.COMMENT) return !notes?.trim()
  if (!staff) return true
  if (type === costReportRowTypeEnum.TIME) {
    const workOk = !work_start === !work_end
    const reportedOk = !reported_start === !reported_end
    const travelOk = !travel_start === !travel_end
    const pauseOk = !pause_start === !pause_end
    const anyTime = work_minutes || reported_minutes || travel_minutes || pause_minutes
    return !workOk || !reportedOk || !travelOk || !pauseOk || !anyTime
  }
  if (type === costReportRowTypeEnum.EXPENSE) {
    return tag == null || quantity == null || quantity === '' || isReceiptMissing.value
  }
  return true
})

// ─── Save ──────────────────────────────────────────────────────────────────────

const onSave = async() => {
  isUpdating.value = true
  try {
    const { data: saved } = await accountingStore.upsertCostReportRowAsync(editItem.value)
    notifySuccess(editItem.value.id ? i18n.t('info.updatedSuccessfully') : i18n.t('info.createdSuccessfully'))
    emit('update', saved)
  } finally {
    isUpdating.value = false
  }
}

const onDownloadAttachment = (file) => accountingStore.downloadCostReportRowFileAsync(file.id, file.name)

const projectOptions = computed(() => [{ id: null, name: i18n.t('common.generalExpenses') }, ...staffProjects.value])
const selectedTag = computed(() => accountingStore.costReportRowTagById.get(editItem.value.tag))
const isReceiptMissing = computed(() => !!selectedTag.value?.receipt_required && !editItem.value.row_files?.length && !editItem.value.row_urls?.length)

if (isNewRow && editItem.value.type === costReportRowTypeEnum.EXPENSE && editItem.value.currency_ref == null) {
  editItem.value.currency_ref = (props.project?.currency_ref ?? webSiteStore.getDefaultCurrency()?.id) || null
}
const savedRow = ref(useClone(props.modelValue))
const { onCurrencyChange, onExchangeRateChange } = useExchangeRateEdit(editItem, savedRow)
const rowCurrency = computed(() => webSiteStore.currencies.find(currency => currency.id === editItem.value.currency_ref) || webSiteStore.getDefaultCurrency())
const isForeignCurrency = computed(() => editItem.value.currency_ref != null && !isReferenceCurrency(editItem.value.currency_ref))
const onCostChange = (value) => { editItem.value.cost = value }

const onTagChange = (tagId) => {
  editItem.value.tag = tagId
  const unitOfMeasureRef = accountingStore.costReportRowTagById.get(tagId)?.unit_of_measure_ref
  if (unitOfMeasureRef != null) editItem.value.unit_of_measure_ref = unitOfMeasureRef
}
const onProjectChange = (projectId) => {
  editItem.value.project = projectId
  if (!projectId) editItem.value.is_rebillable = false
}
const total = computed(() => {
  const { quantity, cost } = editItem.value
  return quantity != null && cost != null ? quantity * cost : null
})
const { lockMessage: rowLockMessage } = useLockedCostReportRow()
const isLocked = provideReadonlyForm(() => editItem.value.lock_reason != null)
const lockMessage = computed(() => rowLockMessage(editItem.value))

const getHeaderTitle = computed(() => {
  const action = editItem.value.id ? i18n.t('common.edit') : i18n.t('common.add')
  const typeLabels = {
    [costReportRowTypeEnum.TIME]: i18n.t('common.costReportTime'),
    [costReportRowTypeEnum.EXPENSE]: i18n.t('common.expense'),
    [costReportRowTypeEnum.COMMENT]: i18n.t('common.comment'),
  }
  const type = typeLabels[editItem.value.type].toLocaleLowerCase()
  return `${action} ${type}`
})

const isComment = computed(() => editItem.value.type === costReportRowTypeEnum.COMMENT)

const saveAttempted = ref(false)

const emit = defineEmits(['update', 'cancel'])
useNavigator(() => emit('cancel'))
useEscapeClose(isUpdated, () => emit('cancel'))
const onCancel = () => emit('cancel')


</script>
<template>
  <q-card class="dialog-card-flex">
    <navigate-back
      :text="getHeaderTitle"
      @cancel="onCancel"
    />
    <q-card-section class="scroll col q-pa-none dialog-card-content-scroll dialog-card-content">
      <q-form ref="form" lazy-rules="ondemand">
        <q-card-section class="q-pt-none">
          <q-item-label header class="row items-center q-px-none q-pb-xs">
            <q-icon
              name="mdi-account"
              class="q-mr-sm"
              color="primary"
              size="sm"
            />
            <span class="dialog-card-section-title">{{ i18n.t('common.basicInfo') }}</span>
          </q-item-label>
          <div class="q-ml-sm row q-col-gutter-sm">
            <div v-if="editItem.invoice_number || editItem.expense_claim_number" class="col-12 row q-gutter-sm">
              <q-chip
                v-if="editItem.invoice_number"
                icon="mdi-file-document-check-outline"
                color="blue-1"
                :label="`${i18n.t('common.invoice')} ${editItem.invoice_number}`"
              />
              <q-chip
                v-if="editItem.expense_claim_number"
                icon="mdi-wallet-outline"
                color="green-1"
                :label="`${i18n.t('common.expenseClaim')} ${editItem.expense_claim_number}`"
              />
            </div>
            <div v-if="lockMessage" class="col-12">
              <info-box :tone="infoBoxToneEnum.WARNING" :message="lockMessage" />
            </div>
            <q-select
              v-if="webSiteStore.can(webSiteStore.permissionFlagEnum.FINANCIALS)"
              :readonly="isLocked"
              :model-value="editItem.staff"
              :class="isMobile ? 'col-12' : 'col-6'"
              :label="i18n.t('common.staff')"
              option-value="id"
              option-label="surname_name"
              emit-value
              map-options
              :options="isComment || !props.project ? planningStore.staff : projectStaff"
              :clearable="isComment"
              :rules="isComment ? [] : [val => !!val || i18n.t('common.required')]"
              lazy-rules="ondemand"
              @update:model-value="onStaffChange"
            />
            <q-input
              v-else
              :model-value="currentUserStaffName"
              :class="isMobile ? 'col-12' : 'col-6'"
              :label="i18n.t('common.staff')"
              readonly
            />
            <input-date-selector
              :model-value="editItem.date"
              clearable
              :range="false"
              :class="isMobile ? 'col-12' : 'col-6'"
              :label="i18n.t('common.date')"
              @update:model-value="onDateChange"
            />
          </div>
        </q-card-section>
        <q-card-section v-if="editItem.type === costReportRowTypeEnum.TIME" class="q-pt-none">
          <q-item-label header class="row items-center q-px-none q-pb-xs">
            <q-icon
              name="mdi-clock-outline"
              class="q-mr-sm"
              color="primary"
              size="sm"
            />
            <span class="dialog-card-section-title">{{ i18n.t('common.timeInfo') }}</span>
          </q-item-label>
          <div v-if="saveAttempted && !editItem.work_minutes && !editItem.reported_minutes && !editItem.travel_minutes && !editItem.pause_minutes" class="q-ml-sm q-mb-xs text-negative text-caption">
            {{ i18n.t('error.atLeastOneTimeRequired') }}
          </div>
          <div class="q-ml-sm q-mb-xs text-caption text-weight-medium text-primary"><q-icon name="mdi-timer-outline" class="q-mr-xs" /> {{ i18n.t('common.reportedTime') }}</div>
          <div class="q-ml-sm row q-col-gutter-sm q-mb-sm">
            <time-selector
              :model-value="timeToISO(editItem.reported_start)"
              :label="isMobile ? i18n.t('common.start') : i18n.t('common.reportedTimeStart')"
              :class="isMobile ? 'col-6' : 'col-5'"
              :rules="[requiredIfPaired('reported_end')]"
              lazy-rules="ondemand"
              clearable
              @update:model-value="onReportedStartUpdate"
            />
            <time-selector
              :model-value="timeToISO(editItem.reported_end)"
              :label="isMobile ? i18n.t('common.end') : i18n.t('common.reportedTimeEnd')"
              :class="isMobile ? 'col-6' : 'col-5'"
              :min="timeToISO(editItem.reported_start)"
              :rules="[requiredIfPaired('reported_start')]"
              lazy-rules="ondemand"
              end
              clearable
              @update:model-value="onReportedEndUpdate"
            />
            <q-input
              :key="hoursKey"
              :readonly="isLocked"
              :model-value="reportedHours"
              :label="i18n.t('common.totalHours')"
              :class="isMobile ? 'col-12' : 'col-2'"
              type="number"
              min="0"
              :max="MAX_HOURS"
              step="0.25"
              :suffix="reportedHours != null ? 'h' : ''"
              input-class="text-right"
              @update:model-value="val => onHoursChange('reported_start', 'reported_end', 'reported_minutes', val)"
            />
          </div>
          <div class="q-ml-sm q-mb-xs text-caption text-weight-medium text-primary"><q-icon name="mdi-briefcase-outline" class="q-mr-xs" /> {{ i18n.t('common.workTime') }}</div>
          <div class="q-ml-sm row q-col-gutter-sm q-mb-sm">
            <time-selector
              :model-value="timeToISO(editItem.work_start)"
              :label="isMobile ? i18n.t('common.start') : i18n.t('common.workTimeStart')"
              :class="isMobile ? 'col-6' : 'col-5'"
              :rules="[requiredIfPaired('work_end')]"
              lazy-rules="ondemand"
              clearable
              @update:model-value="onWorkStartUpdate"
            />
            <time-selector
              :model-value="timeToISO(editItem.work_end)"
              :label="isMobile ? i18n.t('common.end') : i18n.t('common.workTimeEnd')"
              :class="isMobile ? 'col-6' : 'col-5'"
              :min="timeToISO(editItem.work_start)"
              :rules="[requiredIfPaired('work_start')]"
              lazy-rules="ondemand"
              end
              clearable
              @update:model-value="onWorkEndUpdate"
            />
            <q-input
              :key="hoursKey"
              :readonly="isLocked"
              :model-value="workHours"
              :label="i18n.t('common.totalHours')"
              :class="isMobile ? 'col-12' : 'col-2'"
              type="number"
              min="0"
              :max="MAX_HOURS"
              step="0.25"
              :suffix="workHours != null ? 'h' : ''"
              input-class="text-right"
              @update:model-value="val => onHoursChange('work_start', 'work_end', 'work_minutes', val)"
            />
          </div>
          <div class="q-ml-sm q-mb-xs text-caption text-weight-medium text-primary"><q-icon name="mdi-car-outline" class="q-mr-xs" /> {{ i18n.t('common.travelInfo') }}</div>
          <div class="q-ml-sm row q-col-gutter-sm q-mb-sm">
            <time-selector
              :model-value="timeToISO(editItem.travel_start)"
              :label="isMobile ? i18n.t('common.start') : i18n.t('common.travelTimeStart')"
              :class="isMobile ? 'col-6' : 'col-5'"
              :rules="[requiredIfPaired('travel_end')]"
              lazy-rules="ondemand"
              clearable
              @update:model-value="onTravelStartUpdate"
            />
            <time-selector
              :model-value="timeToISO(editItem.travel_end)"
              :label="isMobile ? i18n.t('common.end') : i18n.t('common.travelTimeEnd')"
              :class="isMobile ? 'col-6' : 'col-5'"
              :min="timeToISO(editItem.travel_start)"
              :rules="[requiredIfPaired('travel_start')]"
              lazy-rules="ondemand"
              end
              clearable
              @update:model-value="onTravelEndUpdate"
            />
            <q-input
              :key="hoursKey"
              :readonly="isLocked"
              :model-value="travelHours"
              :label="i18n.t('common.totalHours')"
              :class="isMobile ? 'col-12' : 'col-2'"
              type="number"
              min="0"
              :max="MAX_HOURS"
              step="0.25"
              :suffix="travelHours != null ? 'h' : ''"
              input-class="text-right"
              @update:model-value="val => onHoursChange('travel_start', 'travel_end', 'travel_minutes', val)"
            />
          </div>
          <div class="q-ml-sm q-mb-xs text-caption text-weight-medium text-primary"><q-icon name="mdi-pause-circle-outline" class="q-mr-xs" /> {{ i18n.t('common.pauseInfo') }}</div>
          <div class="q-ml-sm row q-col-gutter-sm q-mb-sm">
            <time-selector
              :model-value="timeToISO(editItem.pause_start)"
              :label="isMobile ? i18n.t('common.start') : i18n.t('common.pauseStart')"
              :class="isMobile ? 'col-6' : 'col-5'"
              :rules="[requiredIfPaired('pause_end')]"
              lazy-rules="ondemand"
              clearable
              @update:model-value="onPauseStartUpdate"
            />
            <time-selector
              :model-value="timeToISO(editItem.pause_end)"
              :label="isMobile ? i18n.t('common.end') : i18n.t('common.pauseEnd')"
              :class="isMobile ? 'col-6' : 'col-5'"
              :min="timeToISO(editItem.pause_start)"
              :rules="[requiredIfPaired('pause_start')]"
              lazy-rules="ondemand"
              end
              clearable
              @update:model-value="onPauseEndUpdate"
            />
            <q-input
              :key="hoursKey"
              :readonly="isLocked"
              :model-value="pauseHours"
              :label="i18n.t('common.totalHours')"
              :class="isMobile ? 'col-12' : 'col-2'"
              type="number"
              min="0"
              :max="MAX_HOURS"
              step="0.25"
              :suffix="pauseHours != null ? 'h' : ''"
              input-class="text-right"
              @update:model-value="val => onHoursChange('pause_start', 'pause_end', 'pause_minutes', val)"
            />
          </div>
          <div class="q-ml-sm row q-col-gutter-sm">
            <form-section-title class="col-12" :label="i18n.t('common.details')" />
            <q-input
              v-model="editItem.notes"
              :readonly="isLocked"
              class="col-12"
              :label="i18n.t('common.notes')"
              autogrow
            />
          </div>
        </q-card-section>
        <q-card-section v-if="editItem.type === costReportRowTypeEnum.EXPENSE" class="q-pt-none">
          <q-item-label header class="row items-center q-px-none q-pb-xs">
            <q-icon
              name="mdi-cash"
              class="q-mr-sm"
              color="primary"
              size="sm"
            />
            <span class="dialog-card-section-title">{{ i18n.t('common.expenseInfo') }}</span>
          </q-item-label>
          <div class="q-ml-sm row q-col-gutter-sm">
            <q-select
              :readonly="isLocked"
              :model-value="editItem.tag"
              :class="isMobile ? 'col-12' : 'col-6'"
              :label="i18n.t('common.expenseItem')"
              option-value="id"
              option-label="name"
              emit-value
              map-options
              :options="accountingStore.getLocalizedCostReportRowTags()"
              :rules="[val => val != null || i18n.t('common.required')]"
              lazy-rules="ondemand"
              @update:model-value="onTagChange"
            />
            <q-select
              v-if="!props.project"
              :readonly="isLocked"
              :model-value="editItem.project"
              :class="isMobile ? 'col-12' : 'col-6'"
              :label="i18n.t('common.project')"
              option-value="id"
              option-label="name"
              emit-value
              map-options
              :options="projectOptions"
              @update:model-value="onProjectChange"
            />
            <form-section-title class="col-12" :label="i18n.t('common.amount')" />
            <q-input
              :readonly="isLocked"
              :model-value="editItem.quantity"
              :class="isMobile ? 'col-4' : 'col-2'"
              :label="i18n.t('common.quantityAlt')"
              min="0"
              type="number"
              :rules="[val => (val != null && val !== '') || i18n.t('common.required')]"
              lazy-rules="ondemand"
              @update:model-value="val => { editItem.quantity = val !== '' && val != null ? Number(val) : null }"
            />
            <q-select
              v-model="editItem.unit_of_measure_ref"
              :readonly="isLocked"
              :class="isMobile ? 'col-4' : 'col-2'"
              :label="i18n.t('common.unitOfMeasureAlt')"
              option-value="id"
              option-label="name"
              emit-value
              map-options
              :options="webSiteStore.getLocalizedUnitsOfMeasure()"
            />
            <currency-input
              :model-value="editItem.cost"
              :class="isMobile ? 'col-4' : 'col-3'"
              :label="i18n.t('common.cost')"
              :suffix="rowCurrency?.symbol"
              @update:model-value="onCostChange"
            />
            <currency-select
              :model-value="editItem.currency_ref"
              :class="isMobile ? 'col-12' : 'col-3'"
              :clearable="false"
              @update:model-value="onCurrencyChange"
            />
            <q-input
              :model-value="total != null ? formatCurrencyValue(total) : null"
              :class="isMobile ? 'col-12' : 'col-2'"
              :label="i18n.t('common.total')"
              readonly
              :suffix="total != null ? rowCurrency?.symbol : ''"
              input-class="text-right"
            />
            <div v-if="isForeignCurrency" class="col-12">
              <exchange-rate-panel
                :model-value="editItem"
                :currency-ref="editItem.currency_ref"
                :no-rate-hint="i18n.t('common.expenseRateOnSaveHint')"
                @update:model-value="onExchangeRateChange"
              />
            </div>
            <form-section-title class="col-12" :label="i18n.t('common.details')" />
            <q-input
              v-model="editItem.notes"
              :readonly="isLocked"
              class="col-12"
              :label="i18n.t('common.notes')"
              autogrow
            />
            <div class="col-12">
              <file-url-edit
                bordered
                caption
                :model-value="editItem.row_files"
                :urls="editItem.row_urls"
                :mobile="isMobile"
                @update:model-value="editItem.row_files = $event"
                @update:urls="editItem.row_urls = $event"
                @download="onDownloadAttachment"
              />
              <div
                v-if="isReceiptMissing"
                class="text-caption q-pt-xs"
                :class="saveAttempted ? 'text-negative' : 'text-grey-7'"
              >
                <q-icon name="mdi-paperclip" class="q-mr-xs" />{{ i18n.t('common.expenseItemReceiptRequired') }}
              </div>
            </div>
            <q-toggle
              v-if="editItem.project"
              v-model="editItem.is_rebillable"
              :disable="isLocked"
              class="col-12"
              :label="i18n.t('common.rebillable')"
            />
          </div>
        </q-card-section>
        <q-card-section v-if="isComment" class="q-pt-none">
          <div class="q-ml-sm row q-col-gutter-sm">
            <q-input
              v-model="editItem.notes"
              :readonly="isLocked"
              class="col-12"
              :label="i18n.t('common.comment')"
              autogrow
              autofocus
              :rules="[val => !!(val && val.trim()) || i18n.t('common.required')]"
              lazy-rules="ondemand"
            />
          </div>
        </q-card-section>
      </q-form>
    </q-card-section>
    <q-card-actions v-if="!isLocked" class="align-center dialog-card-bottom-bar">
      <q-btn
        class="large-btn"
        color="positive"
        :disable="false"
        :label="editItem.id ? i18n.t('common.update') : i18n.t('common.add')"
        :loading="isUpdating"
        @click="saveAttempted = true; form.validate().then(success => !hasErrors && success && onSave())"
      />
    </q-card-actions>
  </q-card>
</template>
